import { useState, type ClipboardEvent, type FormEvent, type KeyboardEvent, type ReactNode, type RefObject } from 'react'
import { Avatar, Switch } from '../../components/ui'
import { FramedImage } from '../../components/FramedImage'
import { insertPlainText } from '../../components/editor/caret'
import type { ImageFrame } from '../../lib/types'

interface Props {
  coverUrl: string | null
  /** The cover's crop & position (null = the default framing). */
  coverFrame: ImageFrame | null
  coverBusy: boolean
  onPickCover: () => void
  onDropCover: (file: File) => void
  onRemoveCover: () => void
  /** Open the crop & position editor for the cover. */
  onCropCover: () => void
  titleRef: RefObject<HTMLHeadingElement | null>
  subtitleRef: RefObject<HTMLParagraphElement | null>
  onTitleInput: (e: FormEvent<HTMLElement>) => void
  onSubtitleInput: (e: FormEvent<HTMLElement>) => void
  onTitleKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  onSubtitleKeyDown: (e: KeyboardEvent<HTMLElement>) => void
  avatarUrl: string | null | undefined
  authorName: string
  /** "Sep 8, 2026 · 6 min read", after the author's name */
  byline: string
  showTimes: boolean
  onShowTimes: (v: boolean) => void
  /** The narration card (design 4a's AUDIO card), always under the byline. */
  narration: ReactNode
  /** The audio dock is open: nothing here is editable (the narration card stays live). */
  locked?: boolean
  /** Focus mode: just the title and subtitle, so the writing starts at the top of the page. */
  focusMode?: boolean
}

/** Set as an attribute: React 18 (the runtime here) has no boolean `inert` prop. */
const inertWhen = (locked: boolean) => (el: HTMLElement | null) => {
  el?.toggleAttribute('inert', locked)
}

const plainPaste = (e: ClipboardEvent<HTMLElement>) => {
  e.preventDefault()
  insertPlainText(e.clipboardData.getData('text/plain').replace(/\s+/g, ' '))
}

/** Cover, title, subtitle, byline and narration, as design 4a sets them. */
export function ComposerHeader(props: Props) {
  const { coverUrl, coverFrame, coverBusy, onPickCover, onDropCover, onRemoveCover, onCropCover, titleRef, subtitleRef, locked = false, focusMode = false } = props
  const [coverDrag, setCoverDrag] = useState(false)
  const lock = inertWhen(locked)
  return (
    <header>
      {focusMode ? null : coverUrl ? (
        <div ref={lock} data-focus-unit="" className="group relative mb-[22px] h-[200px] overflow-hidden rounded-2xl bg-muted sm:h-[320px]">
          <FramedImage src={coverUrl} frame={coverFrame} draggable={false} className="size-full" />
          <div className="absolute bottom-3 right-3 flex gap-1.5 font-sans text-[13px] font-medium opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
            <button type="button" onClick={onCropCover} disabled={coverBusy} className="rounded-full bg-scrim/75 px-3 py-1.5 text-on-scrim disabled:opacity-60">
              Reposition
            </button>
            <button type="button" onClick={onPickCover} className="rounded-full bg-scrim/75 px-3 py-1.5 text-on-scrim">
              {coverBusy ? 'Uploading…' : 'Replace'}
            </button>
            <button type="button" onClick={onRemoveCover} className="rounded-full bg-scrim/75 px-3 py-1.5 text-on-scrim">
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          ref={lock}
          type="button"
          data-focus-unit=""
          onClick={onPickCover}
          onDragOver={(e) => {
            if (!e.dataTransfer.types.includes('Files')) return
            e.preventDefault()
            e.stopPropagation()
            setCoverDrag(true)
          }}
          onDragLeave={() => setCoverDrag(false)}
          onDrop={(e) => {
            if (!e.dataTransfer.files.length) return
            e.preventDefault()
            e.stopPropagation()
            setCoverDrag(false)
            onDropCover(e.dataTransfer.files[0])
          }}
          className={`mb-[22px] flex h-[120px] w-full items-center justify-center gap-2 rounded-2xl border border-dashed font-sans text-[13px] font-medium text-muted-foreground transition-colors sm:h-[220px] ${
            coverDrag ? 'border-primary bg-accent/20' : 'border-border bg-foreground/3 hover:bg-foreground/5'
          }`}
        >
          <span aria-hidden className="size-3.5 rounded-[3px] border-[1.5px] border-muted-foreground" />
          {coverBusy ? 'Uploading…' : coverDrag ? 'Drop to set cover' : 'Add cover image'}
        </button>
      )}

      <h1
        ref={titleRef}
        data-focus-unit=""
        contentEditable={!locked}
        suppressContentEditableWarning
        role="textbox"
        aria-label="Title"
        data-ph="Title"
        onInput={props.onTitleInput}
        onPaste={plainPaste}
        onKeyDown={props.onTitleKeyDown}
        className="wr-t-title m-0 mb-2.5 min-h-[1.1em] outline-none"
      />
      <p
        ref={subtitleRef}
        data-focus-unit=""
        contentEditable={!locked}
        suppressContentEditableWarning
        role="textbox"
        aria-label="Subtitle"
        data-ph="Subtitle, optional"
        onInput={props.onSubtitleInput}
        onPaste={plainPaste}
        onKeyDown={props.onSubtitleKeyDown}
        className="wr-t-subtitle m-0 mb-[18px] min-h-[1.35em] outline-none"
      />

      {!focusMode && (
        <>
          <div
            ref={lock}
            data-focus-unit=""
            className="mb-[18px] flex flex-wrap items-center gap-x-2.5 gap-y-2 border-b border-border/60 pb-5 font-sans text-[13px] font-medium text-muted-foreground"
          >
            <Avatar src={props.avatarUrl} name={props.authorName} size={24} />
            <span className="font-display text-[14px] font-normal">
              <span className="text-foreground">{props.authorName}</span> · {props.byline}
            </span>
            <Switch label="Show times on thoughts" checked={props.showTimes} onChange={props.onShowTimes} className="ml-auto text-muted-foreground" />
          </div>

          <div data-focus-unit="" className="mb-[34px] empty:hidden">{props.narration}</div>
        </>
      )}
    </header>
  )
}
